/**
 * SMTP Yonetimi sekmesi - SADECE SUPER ADMIN.
 *
 * Aciklama:
 * - Liste: Tum CompanySmtpSetting kayitlarinin listesi (Global ve sirkete ozel)
 * - Yeni eklenebilir, mevcut duzenlenebilir / silinebilir
 * - Parolalar asla geri donmez (HasPassword: true/false gosterilir)
 * - Yeni parola girilirse update yapilir; bos birakilirsa mevcut parola korunur
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../../../contexts/AuthContext';
import type { CompanySmtp, CompanySmtpUpsertRequest } from '../types';
import {
  deleteSmtp,
  listSmtp,
  upsertSmtp,
} from '../services/mailReportsApi';

interface FormState {
  Id: number | null;
  CompanyId: string | null;
  Host: string;
  Port: number;
  UseSsl: boolean;
  Username: string;
  Password: string;
  FromEmail: string;
  FromName: string;
  IsActive: boolean;
}

const EMPTY_FORM: FormState = {
  Id: null,
  CompanyId: null,
  Host: 'smtp.gmail.com',
  Port: 587,
  UseSsl: true,
  Username: '',
  Password: '',
  FromEmail: '',
  FromName: 'AQUASOFT GTS',
  IsActive: true,
};

const SmtpSettingsTab: React.FC = () => {
  const { isSuperAdmin } = useAuth();

  const [list, setList] = React.useState<CompanySmtp[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  const [snackbar, setSnackbar] = React.useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });

  const reload = React.useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listSmtp();
      setList(data);
    } catch (err: any) {
      setError(err?.response?.data?.Message || err?.message || 'Yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  if (!isSuperAdmin) {
    return (
      <Alert severity="warning">
        Bu sekme yalnızca SuperAdmin kullanıcılara açıktır.
      </Alert>
    );
  }

  const openNew = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (s: CompanySmtp) => {
    setForm({
      Id: s.Id,
      CompanyId: s.CompanyId,
      Host: s.Host,
      Port: s.Port,
      UseSsl: s.UseSsl,
      Username: s.Username,
      Password: '',
      FromEmail: s.FromEmail,
      FromName: s.FromName,
      IsActive: s.IsActive,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.Host || !form.Username || !form.FromEmail) {
      setSnackbar({ open: true, severity: 'error', message: 'Host, Username, Gönderici Email zorunlu.' });
      return;
    }
    if (!form.Id && !form.Password) {
      setSnackbar({ open: true, severity: 'error', message: 'Yeni SMTP için parola girmelisiniz.' });
      return;
    }

    const payload: CompanySmtpUpsertRequest = {
      CompanyId: form.CompanyId,
      Host: form.Host.trim(),
      Port: form.Port,
      UseSsl: form.UseSsl,
      Username: form.Username.trim(),
      Password: form.Password || null,
      FromEmail: form.FromEmail.trim(),
      FromName: form.FromName.trim(),
      IsActive: form.IsActive,
    };

    setSaving(true);
    try {
      await upsertSmtp(payload);
      setSnackbar({ open: true, severity: 'success', message: 'SMTP ayarı kaydedildi.' });
      setDialogOpen(false);
      reload();
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.Message || err?.message || 'Kaydedilemedi',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s: CompanySmtp) => {
    const label = s.CompanyId ? `Şirket: ${s.CompanyId}` : 'GLOBAL DEFAULT';
    const ok = window.confirm(`SMTP kaydını silmek istediğinizden emin misiniz? (${label})`);
    if (!ok) return;
    setDeletingId(s.Id);
    try {
      await deleteSmtp(s.Id);
      setSnackbar({ open: true, severity: 'success', message: 'SMTP kaydı silindi.' });
      reload();
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.Message || err?.message || 'Silinemedi',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="h6">SMTP Ayarları (SuperAdmin)</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Yenile">
          <span>
            <IconButton size="small" onClick={reload} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Yeni SMTP
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Şifre alanını boş bırakırsanız önceki şifre korunur.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Şirket</TableCell>
              <TableCell>Host</TableCell>
              <TableCell>Port</TableCell>
              <TableCell>SSL</TableCell>
              <TableCell>Gönderici</TableCell>
              <TableCell>Şifre</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Henüz SMTP ayarı yok. "Yeni SMTP" ile ekleyin.
                </TableCell>
              </TableRow>
            ) : (
              list.map((s) => (
                <TableRow key={s.Id} hover>
                  <TableCell>
                    {s.CompanyId ? (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {s.CompanyId}
                      </Typography>
                    ) : (
                      <Chip size="small" label="GLOBAL DEFAULT" color="primary" />
                    )}
                  </TableCell>
                  <TableCell>{s.Host}</TableCell>
                  <TableCell>{s.Port}</TableCell>
                  <TableCell>
                    {s.UseSsl ? <Chip size="small" label="SSL/TLS" color="success" /> : <Chip size="small" label="Yok" />}
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">{s.FromEmail}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.FromName}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {s.HasPassword ? (
                      <Chip size="small" label="Var" color="success" variant="outlined" />
                    ) : (
                      <Chip size="small" label="Yok" color="error" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {s.IsActive ? (
                      <Chip size="small" label="Aktif" color="success" />
                    ) : (
                      <Chip size="small" label="Pasif" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Düzenle">
                        <IconButton size="small" onClick={() => openEdit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sil">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(s)}
                            disabled={deletingId === s.Id}
                          >
                            {deletingId === s.Id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.Id ? 'SMTP Düzenle' : 'Yeni SMTP'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Şirket ID (boş = global default)"
                value={form.CompanyId || ''}
                onChange={(e) => setForm({ ...form, CompanyId: e.target.value.trim() || null })}
                size="small"
                fullWidth
                helperText={form.CompanyId ? "Bu SMTP yalnızca bu şirketin joblarında kullanılır" : "Tüm şirketlerin (kendi ayarı olmayan) default'u olur"}
              />
            </Grid>

            <Grid item xs={8}>
              <TextField
                label="Host *"
                value={form.Host}
                onChange={(e) => setForm({ ...form, Host: e.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Port *"
                type="number"
                value={form.Port}
                onChange={(e) => setForm({ ...form, Port: parseInt(e.target.value, 10) || 587 })}
                size="small"
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.UseSsl}
                    onChange={(e) => setForm({ ...form, UseSsl: e.target.checked })}
                  />
                }
                label="SSL/TLS Kullan"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.IsActive}
                    onChange={(e) => setForm({ ...form, IsActive: e.target.checked })}
                    color="success"
                  />
                }
                label={form.IsActive ? 'Aktif' : 'Pasif'}
                sx={{ ml: 2 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Kullanıcı Adı *"
                value={form.Username}
                onChange={(e) => setForm({ ...form, Username: e.target.value })}
                size="small"
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={form.Id ? 'Şifre (boş = değişiklik yok)' : 'Şifre *'}
                type="password"
                value={form.Password}
                onChange={(e) => setForm({ ...form, Password: e.target.value })}
                size="small"
                fullWidth
                autoComplete="new-password"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Gönderici Email *"
                value={form.FromEmail}
                onChange={(e) => setForm({ ...form, FromEmail: e.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Gönderici Adı"
                value={form.FromName}
                onChange={(e) => setForm({ ...form, FromName: e.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            İptal
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SmtpSettingsTab;
